import { useState, useEffect } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { formatNumber } from "../../../utils/format.js";
import Input from "./Input.js";
import { formatCurrency } from "../../../utils/formatCurrency.js";
import DownloadPDF from "../plan-de-pagos/downloadPDF.js";
import SaveSimulator from "../plan-de-pagos/saveSimulator.js";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../../FirebaseConfig.ts";
import { formatCurrencyTable } from "../../../utils/formatCurrencyTable.js";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

function Simulador() {
  const [user, loading] = useAuthState(auth);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [fields, setFields] = useState({
    ammount: "5.000.000",
    tasa: "29.49",
    month: "24",
    dateStart: dayjs().format("YYYY-MM-DD"),
  });

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const loanAmount = fields?.ammount.replace(/\./g, "");
  const annualInterestRate = fields.tasa / 100;
  const monthlyInterestRate = annualInterestRate / 12;
  const loanTermMonths = fields.month;
  const startDate = dayjs(fields.dateStart, "YYYY-MM-DD");

  const monthlyPayment =
    loanAmount *
    (monthlyInterestRate /
      (1 - Math.pow(1 + monthlyInterestRate, -loanTermMonths)));

  const generatePaymentSchedule = () => {
    let balance = loanAmount;
    const schedule = [];
    let totalInterest = 0;
    let paymentDate = startDate;

    for (let i = 1; i <= loanTermMonths; i++) {
      paymentDate = paymentDate.add(1, "month");
      const adjustedDate = paymentDate.date(startDate.date());
      if (adjustedDate.month() !== paymentDate.month()) {
        paymentDate = paymentDate.endOf("month");
      } else {
        paymentDate = adjustedDate;
      }

      const interest = balance * monthlyInterestRate;
      const capital = monthlyPayment - interest;
      balance -= capital;
      const remainingBalance = balance < 0 ? 0 : balance;
      totalInterest += interest;

      schedule.push({
        cuota: i,
        fechaPago: paymentDate.format("DD/MM/YYYY"),
        valorPrestamo: balance + capital,
        cuotaAPagar: monthlyPayment,
        capital: capital,
        interes: interest,
        saldoDeuda: remainingBalance,
      });
    }

    return { schedule, totalInterest };
  };

  const { schedule, totalInterest } = generatePaymentSchedule();
  const totalCredit =
    Number(totalInterest) + Number(fields?.ammount.replace(/\./g, ""));

  const handleInputChange = (e) => {
    const formattedValue = formatCurrency(e.target.value);
    setFields({ ...fields, ammount: formattedValue });
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div
        className={`transition-all duration-700 ease-out ${
          isLoaded ? "opacity-100 " : "opacity-0"
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
              <div className="p-2 bg-[#49b0a8]/10 rounded-xl">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-[#49b0a8]"
                >
                  <path
                    d="M9 7H6a2 2 0 00-2 2v9a2 2 0 002 2h8a2 2 0 002-2v-3m2-13L10 9m6-6h-4m4 0v4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              Simulador de Crédito
            </h1>
            <p className="text-gray-600">
              Calcula tu plan de pagos y conoce todos los detalles de tu crédito
            </p>
          </div>

          <div className="flex gap-3 items-center">
            <DownloadPDF
              data={{ ...fields, start_date: dayjs().format("YYYY-MM-DD") }}
            />
            {loading || !user ? (
              <button
                className={`
                  group relative overflow-hidden bg-gray-400 text-white px-6 py-3 rounded-xl
                  font-medium transition-all duration-300 ease-out cursor-not-allowed
                  flex items-center gap-2
                `}
                disabled
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Guardar Simulación
              </button>
            ) : (
              <SaveSimulator
                data={{ ...fields, start_date: dayjs().format("YYYY-MM-DD") }}
                user={user}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>

      {/* Configuration and Results Section */}
      <div
        className={`transition-all duration-700 ease-out delay-200 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration Panel */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Configuración del Crédito
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Input
                  label="Valor del préstamo"
                  placeholder="$0"
                  type="text"
                  onChange={handleInputChange}
                  value={fields?.ammount}
                />
              </div>

              <div className="space-y-2">
                <Input
                  label="Tasa de interés anual (%)"
                  placeholder="0.00"
                  type="text"
                  onChange={(e) =>
                    setFields({ ...fields, tasa: e.target.value })
                  }
                  value={fields.tasa}
                />
              </div>

              <div className="space-y-2">
                <Input
                  label="Periodo en meses"
                  placeholder="12"
                  type="text"
                  onChange={(e) =>
                    setFields({ ...fields, month: e.target.value })
                  }
                  value={fields.month}
                />
              </div>

              <div className="space-y-2">
                <Input
                  label="Fecha de inicio"
                  placeholder=""
                  type="date"
                  onChange={(e) =>
                    setFields({ ...fields, dateStart: e.target.value })
                  }
                  value={fields.dateStart}
                />
              </div>
            </div>
          </div>

          {/* Results Panel */}
          <div className="bg-gradient-to-br from-[#49b0a8] to-[#3a8a84] rounded-xl shadow-sm p-6 text-white">
            <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 11H7a2 2 0 00-2 2v7a2 2 0 002 2h2a2 2 0 002-2v-7a2 2 0 00-2-2zM13 7H11a2 2 0 00-2 2v11a2 2 0 002 2h2a2 2 0 002-2V9a2 2 0 00-2-2zM17 3H15a2 2 0 00-2 2v15a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Resumen del Crédito
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <div className="text-sm opacity-80 mb-1">Pago mensual</div>
                <div className="text-2xl font-bold">
                  {formatNumber(schedule[0]?.cuotaAPagar.toFixed(0))}
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <div className="text-sm opacity-80 mb-1">Número de pagos</div>
                <div className="text-2xl font-bold">{fields.month}</div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <div className="text-sm opacity-80 mb-1">Total intereses</div>
                <div className="text-2xl font-bold">
                  {formatNumber(totalInterest.toFixed(0))}
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <div className="text-sm opacity-80 mb-1">Costo total</div>
                <div className="text-2xl font-bold">
                  {formatNumber(totalCredit.toFixed(0))}
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex justify-between text-sm opacity-80 mb-2">
                <span>Capital</span>
                <span>Intereses</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-3">
                <div
                  className="bg-white h-3 rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: `${
                      (Number(fields?.ammount?.replace(/\./g, "")) /
                        totalCredit) *
                      100
                    }%`,
                  }}
                ></div>
              </div>
              <div className="flex justify-between text-xs opacity-70 mt-1">
                <span>
                  {(
                    (Number(fields?.ammount.replace(/\./g, "")) / totalCredit) *
                    100
                  ).toFixed(1)}
                  %
                </span>
                <span>{((totalInterest / totalCredit) * 100).toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Schedule Table */}
      <div
        className={`transition-all duration-700 ease-out delay-400 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Plan de Pagos
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Cronograma detallado de amortización del crédito
            </p>
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Cuota
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Fecha de descuento
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Valor Préstamo
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Cuota a Pagar
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Capital
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Interés
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Saldo Deuda
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {schedule.map((row, index) => (
                  <tr
                    key={row.cuota}
                    className={`
                      transition-all duration-200 hover:bg-gray-50 group
                      ${
                        isLoaded
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-4"
                      }
                    `}
                    style={{
                      animationDelay: `${index * 50}ms`,
                      animation: isLoaded
                        ? "fadeInUp 0.6s ease-out forwards"
                        : "none",
                    }}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600 group-hover:bg-[#49b0a8] group-hover:text-white transition-colors duration-200">
                        {row.cuota}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">
                        {row.fechaPago === "Invalid Date" ? "-" : row.fechaPago}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        ${formatCurrencyTable(row.valorPrestamo)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-[#49b0a8]">
                        ${formatCurrencyTable(row?.cuotaAPagar)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ${formatCurrencyTable(row.capital)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-red-600">
                        ${formatCurrencyTable(row.interes)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        ${formatCurrencyTable(row.saldoDeuda)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4 p-4">
            {schedule.map((row, index) => (
              <div
                key={row.cuota}
                className={`
                  bg-white border border-gray-200 rounded-xl p-4 space-y-3 hover:shadow-md transition-all duration-300
                  ${
                    isLoaded
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                  }
                `}
                style={{
                  animationDelay: `${index * 50}ms`,
                  animation: isLoaded
                    ? "fadeInUp 0.6s ease-out forwards"
                    : "none",
                }}
              >
                <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#49b0a8] rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {row.cuota}
                    </div>
                    <span className="font-medium text-gray-900">
                      Cuota {row.cuota}
                    </span>
                  </div>
                  <span className="text-sm text-gray-600">
                    {row.fechaPago === "Invalid Date" ? "-" : row.fechaPago}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Cuota a pagar:</span>
                    <div className="font-semibold text-[#49b0a8]">
                      ${formatCurrencyTable(row?.cuotaAPagar)}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Capital:</span>
                    <div className="font-semibold text-gray-900">
                      ${formatCurrencyTable(row.capital)}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Interés:</span>
                    <div className="font-semibold text-red-600">
                      ${formatCurrencyTable(row.interes)}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Saldo:</span>
                    <div className="font-semibold text-gray-900">
                      ${formatCurrencyTable(row.saldoDeuda)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default Simulador;
